import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useApi, useWorkspace } from '~/contexts';
import MultiSelect from '../MultiSelect';
import Fuse from 'fuse.js';
import Percent from '../Percent';
import opportunityStageCategories from '~/lookups/opportunity-stage-categories';

export default function OpportunityStageFilter({
  placeholder = 'Opportunity Stage',
  name,
  value,
  onChange,
  opportunityStageCategoryId,
  opportunityPipelineId,
}) {
  const api = useApi();
  const { workspace } = useWorkspace();
  const [data, setData] = useState(null);
  const [options, setOptions] = useState([]);

  const handleSearch = useCallback(
    async (q) => {
      let options = data;
      if (!data) {
        const result = await api.www
          .workspaces(workspace.id)
          .opportunityStages()
          .get({
            q,
            isActive: true,
            statusId: opportunityStageCategoryId ? opportunityStageCategoryId : undefined,
            opportunityPipelineId: opportunityPipelineId ? opportunityPipelineId : undefined,
          });
        setData(result.data);
        options = result.data;
      }
      if (q) {
        options = new Fuse(options, { keys: ['name'], threshold: 0.4 }).search(q).map((result) => result.item);
      }
      setOptions(options);
    },
    [workspace.id, api, data, opportunityStageCategoryId, opportunityPipelineId],
  );

  useEffect(() => {
    setData(null);
    setOptions([]);
  }, [opportunityStageCategoryId, opportunityPipelineId]);

  const groups = useMemo(() => {
    if (!options) return null;

    const statusOrder = ['open', 'won', 'lost'];

    return _(options)
      .groupBy((o) => o.opportunityPipelineId)
      .map((pipelineStages) => {
        const pipeline = pipelineStages[0].opportunityPipeline;

        const statuses = statusOrder
          .map((statusId) => {
            const stages = pipelineStages.filter((s) => s.statusId === statusId);
            if (!stages.length) return null;

            return {
              id: statusId,
              name: opportunityStageCategories[statusId].name,
              stages,
            };
          })
          .filter(Boolean);

        return { pipeline, statuses };
      })

      .sortBy((g) => g.pipeline.name.toLowerCase())
      .value();
  }, [options]);

  return (
    <MultiSelect.Filter
      icon="filter"
      placeholder={placeholder}
      name={name}
      value={value}
      renderValue={(value) => value.map((v) => v.name).join('; ')}
      options={options}
      renderOptions={(opts) => {
        const optionsById = _.keyBy(opts, 'id');
        return groups.map(({ pipeline, statuses }) => (
          <MultiSelect.Group key={pipeline.id} label={pipeline.name}>
            {statuses.map(({ id: statusId, name: statusName, stages }) => (
              <MultiSelect.Group key={statusId} label={statusName}>
                {stages.map((stage) => (
                  <MultiSelect.Option key={stage.id} {...optionsById[stage.id].props}>
                    {stage.name} (
                    <Percent value={stage.probability / 100} minimumFractionDigits={0} maximumFractionDigits={0} />)
                  </MultiSelect.Option>
                ))}
              </MultiSelect.Group>
            ))}
          </MultiSelect.Group>
        ));
      }}
      isLoading={!data}
      onSearch={handleSearch}
      onChange={onChange}
    />
  );
}
